import { useState } from "react";
import { Phone, PhoneOutgoing } from "lucide-react";
import API from "@/lib/api";
import CallNoteDialog from "@/components/CallNoteDialog";

/**
 * Click-to-call wrapper. Shows the patient's phone number as a clickable purple
 * link with a phone icon. On click:
 *   1. Posts to /api/calls/log-attempt to record the dial intent (audit trail)
 *   2. Hands off to the OS via a `tel:` URI — the staff member's installed
 *      Vonage Desktop / Mobile softphone (or any tel: handler) picks it up.
 *   3. Pops the disposition note dialog so staff can log what happened.
 *
 * Use anywhere a patient phone number is shown to staff (admin/prescriber/IT).
 *
 * Props:
 *   - phone:        formatted phone string (display + dial)
 *   - patientId:    user id of the patient being called
 *   - patientName:  optional, used in the note dialog header
 *   - className:    optional extra classes
 *   - compact:      when true, renders as a small icon-only chip
 */
export default function ClickToCallPhone({ phone, patientId, patientName = "", className = "", compact = false }) {
  const [noteDialog, setNoteDialog] = useState(null); // { callId, dialed }
  if (!phone) return <span className="text-slate-400 text-sm">—</span>;

  const dialable = String(phone).replace(/[^\d+]/g, "");

  const handleClick = async (e) => {
    e.preventDefault();
    // Hand off to the OS tel: handler immediately so the dial doesn't wait on the API.
    window.location.href = `tel:${dialable}`;

    // Log the attempt and capture call_id so the note dialog can update it.
    if (patientId) {
      try {
        const { data } = await API.post("/calls/log-attempt", {
          patient_id: patientId,
          dialed_number: dialable,
        });
        if (data?.id) {
          setNoteDialog({ callId: data.id, dialed: dialable });
        }
      } catch {
        // Silent — dial already happened.
      }
    }
  };

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={handleClick}
          title={`Call ${phone}`}
          className={`inline-flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-700 hover:underline ${className}`}
          data-testid={`call-${patientId || dialable}`}
        >
          <Phone className="w-3.5 h-3.5" /> Call
        </button>
      ) : (
        <button
          type="button"
          onClick={handleClick}
          className={`inline-flex items-center gap-1.5 text-purple-700 hover:text-purple-900 hover:underline font-medium ${className}`}
          title="Click to dial via your default phone app (e.g. Vonage)"
          data-testid={`call-phone-${patientId || dialable}`}
        >
          <PhoneOutgoing className="w-3.5 h-3.5" />
          <span>{phone}</span>
        </button>
      )}

      <CallNoteDialog
        open={!!noteDialog}
        onClose={() => setNoteDialog(null)}
        callId={noteDialog?.callId}
        patientName={patientName}
        dialedNumber={noteDialog?.dialed}
      />
    </>
  );
}
